// The landing page's feature slides: one big slide at a time in the site's
// chamfered frame. Swipe (phones), the arrow buttons or a tab moves the strip;
// the strip's scroll position is the single source of truth for which slide is
// active, so every way of moving it stays in step. Native scroll-snap, no library.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Shot } from '@/content/shots';
import { IconChevronLeft, IconChevronRight, IconExternal } from './icons';
import { SectionTitle } from './ui';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');
const pad = (n: number) => String(n).padStart(2, '0');
const motion = (): ScrollBehavior => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

function ArrowButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="group cut-sm bg-line p-px transition-colors hover:bg-gold/60">
      <span className="cut-inner flex h-11 w-11 items-center justify-center bg-panel text-muted transition-colors group-hover:text-gold-bright">
        {children}
      </span>
    </button>
  );
}

export function FeatureShowcase({ shots, eyebrow, title }: { shots: Shot[]; eyebrow: string; title: string }) {
  const track = useRef<HTMLDivElement>(null);
  const tabs = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  activeRef.current = active;
  // Where a button or tab is sending the strip. Quick repeated presses step from
  // here, not from the slide the smooth scroll happens to be passing.
  const target = useRef<number | null>(null);
  const settle = useRef(0);
  // The strip width the scroll position was last read against. While a resize is
  // under way the browser clamps scrollLeft to the new width and fires scroll
  // events BEFORE ResizeObserver runs; read against the new width they name the
  // wrong slide (turning a phone upright from slide 5 landed on slide 11).
  const width = useRef(0);
  const n = shots.length;

  const go = useCallback(
    (i: number) => {
      const el = track.current;
      if (!el) return;
      const next = ((i % n) + n) % n;
      target.current = next;
      el.scrollTo({ left: next * el.clientWidth, behavior: motion() });
    },
    [n],
  );
  const step = (d: number) => go((target.current ?? activeRef.current) + d);

  const onScroll = () => {
    const el = track.current;
    if (!el || !el.clientWidth || el.clientWidth !== width.current) return;
    const i = Math.min(n - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth)));
    if (i !== activeRef.current) setActive(i);
    // A finger can take over mid-flight: once the strip rests, it is the truth.
    window.clearTimeout(settle.current);
    settle.current = window.setTimeout(() => (target.current = null), 200);
  };

  // Keep the active tab in view inside its own row. Scrolls the row only, never
  // the page (scrollIntoView would drag the page to the tabs mid-swipe).
  useEffect(() => {
    const row = tabs.current;
    const tab = row?.children[active] as HTMLElement | undefined;
    if (!row || !tab || row.scrollWidth <= row.clientWidth) return;
    row.scrollTo({ left: tab.offsetLeft - (row.clientWidth - tab.offsetWidth) / 2, behavior: motion() });
  }, [active]);

  // A resize (a phone turning sideways) would leave the strip between two slides.
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      width.current = el.clientWidth;
      el.scrollTo({ left: activeRef.current * el.clientWidth });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      window.clearTimeout(settle.current);
    };
  }, []);

  const shot = shots[active];
  return (
    <section className="pb-20" aria-roledescription="carousel" aria-label={title}>
      <SectionTitle
        eyebrow={eyebrow}
        title={title}
        action={
          <div className="flex items-center gap-3">
            {/* Phones: the tab row already shows where you are, and the title needs the room. */}
            <p className="num mr-1 hidden whitespace-nowrap text-sm font-semibold tracking-[0.16em] text-dim sm:block">
              <span className="text-gold-bright">{pad(active + 1)}</span> / {pad(n)}
            </p>
            <ArrowButton label="Previous feature" onClick={() => step(-1)}>
              <IconChevronLeft size={20} />
            </ArrowButton>
            <ArrowButton label="Next feature" onClick={() => step(1)}>
              <IconChevronRight size={20} />
            </ArrowButton>
          </div>
        }
      />
      <div className="cut-lg bg-line p-px">
        <div className="cut-inner overflow-hidden bg-scrim">
          <div
            ref={track}
            onScroll={onScroll}
            tabIndex={0}
            aria-label="Feature slides. Swipe or scroll sideways."
            className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [scrollbar-width:none] focus-visible:outline-offset-[-2px] [&::-webkit-scrollbar]:hidden"
          >
            {shots.map((s, i) => (
              <div key={s.src} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${n}: ${s.label}`} className="w-full shrink-0 snap-center">
                <img
                  src={s.src}
                  alt={s.alt}
                  width={1280}
                  height={720}
                  loading={i < 2 ? 'eager' : 'lazy'}
                  decoding="async"
                  draggable={false}
                  className="block aspect-video w-full select-none object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <p className="text-lg text-ink/90">{shot.caption}</p>
        <a
          href={shot.src}
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.12em] text-gold hover:text-gold-bright"
        >
          Full size <IconExternal size={15} />
        </a>
      </div>
      <div ref={tabs} className="mt-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] lg:flex-wrap lg:overflow-visible [&::-webkit-scrollbar]:hidden">
        {shots.map((s, i) => (
          <button
            key={s.src}
            type="button"
            onClick={() => go(i)}
            aria-current={i === active ? 'true' : undefined}
            className={cx(
              'min-h-11 shrink-0 border px-4 text-xs font-semibold uppercase tracking-[0.14em] transition-colors',
              i === active ? 'border-gold/60 bg-gold/10 text-gold-bright' : 'border-line text-dim hover:border-line-strong hover:text-ink',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
    </section>
  );
}
